'use client';

import { cn } from '@/lib/utils';
import { Dispatch, SetStateAction, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { IComponentProps } from '@/interfaces/component.interface';
import { ArrowRightIcon } from 'lucide-react';
import { sendEmail } from '@/actions/email.actions';
import { Textarea } from '../ui/textarea';

export default function ContactForm(props: IComponentProps) {
  const { text, lang } = props;

  const [successMessage, setSuccessMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);

  const formSchema = z.object({
    [text.name.field]: z.string().min(2),
    [text.surname.field]: z.string().min(2),
    [text.email.field]: z.string().email().min(5),
    [text.number.field]: z.string().min(2),
    [text.project.field]: z.string().min(10),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const [check, setCheck] = useState(false);

  function onSubmit(values: z.infer<typeof formSchema>) {
    // const body = `Name: ${values.name}\n\nEmail: ${values.email}\n\nSubject: ${values.subject}\n\nMessage: ${values.message}`
    // window.location.href = `mailto:studio@ophirstudio.com?subject=${encodeURIComponent("Contact form Ophir Website")}&body=${encodeURIComponent(body)}`

    sendEmail(values)
      .then(() => setSuccessMessage(true))
      .catch((err) => {
        setErrorMessage(true);
      });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='space-y-[20px] md:space-y-[56px]  2xl:text-[20px] text-[13px]   2xl:w-[790px] w-[530px] sm:mt-0 mt-[56px] sm:mb-32 mb-[196px]'
      >
        <FormField
          control={form.control}
          name={text.name.field as never}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.name.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.surname.field as never}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.surname.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.email.field as never}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.email.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.number.field as never}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={text.number.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={text.project.field as never}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea placeholder={text.project.placeholder} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <CheckBox setCheck={setCheck} check={check} lang={lang} />

        <Button
          type='submit'
          variant='link'
          className='rounded-full !mt-16 px-0 2xl:text-[64px] text-[42px] sm:text-[32px] leading-none tracking-[-0.05em] md:text-[68px] flex items-center gap-2 pt-4'
        >
          <div className='relative'>
            {text.submit}
            <div className='border-b h-[1px] border-[#0D1524] mt-1' />
          </div>
          <ArrowRightIcon size={49} />
        </Button>

        {successMessage && (
          <p className='my-7 text-green-400'>{text.successMessage}</p>
        )}
        {errorMessage && (
          <p className='my-7 text-red-400'>{text.errorMessage}</p>
        )}
      </form>
    </Form>
  );
}

function CheckBox({
  check,
  setCheck,
  lang,
}: {
  check: boolean;
  setCheck: Dispatch<SetStateAction<boolean>>;
  lang: string;
}) {
  return (
    <div
      className='flex items-center gap-2 !mt-8'
      onClick={() => setCheck(!check)}
    >
      <div
        className={cn('w-5 h-5 sm:w-4 sm:h-4 border border-[#0D1524]', {
          'bg-[#0D1524]': check,
        })}
      />
      <p className='text-[16px] sm:text-[12px] leading-[130%]'>
        {lang === 'en' ? (
          <>
            {' '}
            I have read and accept the website’s{' '}
            <span className='font-bold underline'>privacy policy.</span>
          </>
        ) : (
          <>
            Ho letto e accetto l'informativa sulla{' '}
            <span className='underline'>privacy</span> del sito web.
          </>
        )}
      </p>
    </div>
  );
}
